import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as MinusIcon } from "assets/svgs/Minus.svg";
import { ReactComponent as PlusIcon } from "assets/svgs/Plus.svg";

export const DEFAULT_WAIT_DURATION_SECONDS = 5;
export const MIN_WAIT_DURATION_SECONDS = 1;
export const MAX_WAIT_DURATION_SECONDS = 300;

/** Delay before auto-repeat starts while held. */
const HOLD_START_MS = 350;
/** Interval between auto-repeat ticks. */
const HOLD_TICK_MS = 70;

interface WaitDialogProps {
  visible: boolean;
  onConfirm: (durationSeconds: number) => void;
  onClose: () => void;
}

const clampDuration = (value: number) =>
  Math.min(MAX_WAIT_DURATION_SECONDS, Math.max(MIN_WAIT_DURATION_SECONDS, value));

/** ≤60s → `45s`; >60s → `1m 15s` / `2m`. */
export const formatWaitDuration = (seconds: number): string => {
  if (seconds <= 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return rem === 0 ? `${minutes}m` : `${minutes}m ${rem}s`;
};

/** Step size grows the longer the button is held. */
const stepForHoldMs = (heldMs: number): number => {
  if (heldMs < 800) return 1;
  if (heldMs < 1600) return 5;
  if (heldMs < 2800) return 10;
  if (heldMs < 4200) return 25;
  return 50;
};

const WaitDialog: React.FC<WaitDialogProps> = ({ visible, onConfirm, onClose }) => {
  const { t } = useTranslation(["missions", "common"]);
  const [durationSeconds, setDurationSeconds] = useState(DEFAULT_WAIT_DURATION_SECONDS);
  const holdDelayRef = useRef<number | null>(null);
  const holdTickRef = useRef<number | null>(null);
  const holdStartedAtRef = useRef(0);
  const holdDirectionRef = useRef<1 | -1>(1);
  const durationRef = useRef(durationSeconds);

  durationRef.current = durationSeconds;

  const clearHold = () => {
    if (holdDelayRef.current !== null) {
      window.clearTimeout(holdDelayRef.current);
      holdDelayRef.current = null;
    }
    if (holdTickRef.current !== null) {
      window.clearInterval(holdTickRef.current);
      holdTickRef.current = null;
    }
  };

  useEffect(() => {
    if (visible) {
      setDurationSeconds(DEFAULT_WAIT_DURATION_SECONDS);
    } else {
      clearHold();
    }
  }, [visible]);

  useEffect(() => () => clearHold(), []);

  const applyStep = (direction: 1 | -1, step: number) => {
    const next = clampDuration(durationRef.current + direction * step);
    durationRef.current = next;
    setDurationSeconds(next);
    if (
      (direction < 0 && next <= MIN_WAIT_DURATION_SECONDS) ||
      (direction > 0 && next >= MAX_WAIT_DURATION_SECONDS)
    ) {
      clearHold();
    }
  };

  const startHold = (direction: 1 | -1) => {
    clearHold();
    holdDirectionRef.current = direction;
    holdStartedAtRef.current = performance.now();
    applyStep(direction, 1);

    holdDelayRef.current = window.setTimeout(() => {
      holdDelayRef.current = null;
      holdTickRef.current = window.setInterval(() => {
        const heldMs = performance.now() - holdStartedAtRef.current;
        applyStep(holdDirectionRef.current, stepForHoldMs(heldMs));
      }, HOLD_TICK_MS);
    }, HOLD_START_MS);
  };

  const atMin = durationSeconds <= MIN_WAIT_DURATION_SECONDS;
  const atMax = durationSeconds >= MAX_WAIT_DURATION_SECONDS;

  const bindHold = (direction: 1 | -1, disabled: boolean) => ({
    onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      startHold(direction);
    },
    onPointerUp: clearHold,
    onPointerCancel: clearHold,
    onLostPointerCapture: clearHold,
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  });

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      className="w-72 rounded-r10 border border-black80 bg-black100 p-4"
    >
      <p className="mb-3 text-h5 font-semibold">{t("dialogs.waitDuration")}</p>
      <div className="mb-4 flex items-center justify-center gap-3">
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-r3 bg-black70 hover:bg-black60 disabled:cursor-not-allowed disabled:opacity-50 select-none touch-none"
          disabled={atMin}
          aria-label={t("dialogs.decreaseWait")}
          {...bindHold(-1, atMin)}
        >
          <MinusIcon className="h-4 w-4" />
        </button>
        <p className="min-w-[6.5rem] text-center text-h4">{formatWaitDuration(durationSeconds)}</p>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-r3 bg-black70 hover:bg-black60 disabled:cursor-not-allowed disabled:opacity-50 select-none touch-none"
          disabled={atMax}
          aria-label={t("dialogs.increaseWait")}
          {...bindHold(1, atMax)}
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="flex justify-end gap-2">
        <FunctionalButton
          title={t("common:cancel")}
          titleClassName="text-black60"
          className="cancel-button px-4 py-2"
          onClick={onClose}
        />
        <FunctionalButton
          title={t("common:add")}
          className="h-7 rounded-r5 bg-primary2 px-4"
          onClick={() => onConfirm(durationSeconds)}
        />
      </div>
    </AnimatedModal>
  );
};

export default WaitDialog;
